import { ExternalLookupType } from '@/typings';
import type { IndexedExternalLookups } from '@/typings';

import { FormFieldType } from '../typings';
import type { FormField, FormInitialValues } from '../typings';
import type { AddressGroupField, AddressOwner } from './addressLink';
import { describeAddressField } from './addressSummary';
import type { AddressSummaryLookups } from './addressSummary';
import { allowsPoBox, isPoBox } from './poBox';

/** Why a "Same as" option can't be chosen while its target holds a PO box. */
export const PO_BOX_NOT_ACCEPTED_HERE = "PO boxes aren't accepted here";

/** The notice under a linked group's choice once the group it links to holds a PO box. */
export const nowPoBoxMessage = (targetTitle: string): string =>
  `${targetTitle} is now a PO Box. Choose another option.`;

/** The title a group shows above its fields. */
export const addressGroupTitle = (group: AddressGroupField): string =>
  group.type === FormFieldType.Array ? group.title : group.name;

/** The group's own street: its child that searches addresses; undefined when it has none. */
export const findAddressStreet = (
  group: AddressGroupField,
  externalLookups: IndexedExternalLookups,
): FormField | undefined => {
  const fields: readonly FormField[] = group.fields;

  return fields.find(
    field =>
      externalLookups[field.fieldId]?.options?.lookupType ===
      ExternalLookupType.Address,
  );
};

/** True when the group has a street that doesn't take a PO box. */
export const refusesPoBox = (
  group: AddressGroupField,
  externalLookups: IndexedExternalLookups,
): boolean => {
  const street = findAddressStreet(group, externalLookups);

  return (
    street !== undefined &&
    !allowsPoBox(
      externalLookups[street.fieldId]?.options?.additionalLookupArguments,
    )
  );
};

/** True when the owner's street, as the provider sees it, is a PO box. */
export const ownerHoldsPoBox = (
  owner: AddressOwner,
  values: FormInitialValues,
  lookups: AddressSummaryLookups,
): boolean => {
  if (owner.kind !== 'group') return false;

  const street = findAddressStreet(owner.group, lookups.externalLookups);

  return (
    street !== undefined &&
    isPoBox(describeAddressField(owner.group, street.fieldId, values, lookups))
  );
};
